<template>
    <div class="modal fade">
        <div class="modal-dialog">
            <div class="modal-content">
                <!-- Modal Header -->
                <div class="modal-header">
                    <h3 class="modal-title"><i class="fas fa-file-code left-align"></i>{{portfolioCard.title}}</h3>
                    <button type="button" class="close" data-dismiss="modal" @click="closeModal()">&times;</button>
                </div>

                <!-- Modal body -->
                <div class="modal-body">
                    <div class="body-header">
                        <img :src="`assets/portfolio-modals/${portfolioCard.pictureSource}`" />
                        <!-- Time setting -->
                        <h4>
                            <i class="fas fa-clock"></i>
                            {{portfolioCard.from}}<span v-if="portfolioCard.until">&ndash;{{portfolioCard.until}}</span>
                        </h4>

                        <!-- Skills acquired -->
                        <h4>
                            <i class="fas fa-tools"></i>
                            {{portfolioCard.skills}}
                        </h4>
                    </div>

                    <!-- Portfolio story -->
                    <div class="body-text" v-html="portfolioCard.body"></div>
                    <div class="body-text-end">
                        &mdash;<i class="fas fa-paragraph"></i>
                        <i class="fas fa-hourglass-end"></i>&mdash;
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
    methods: {
        ...mapActions(['showPortfolioModal', 'setPortfolioSelected']),
        closeModal: function(){
            this.showPortfolioModal(false);
            this.setPortfolioSelected(null);
        }
    },
    computed: {
        ...mapState(['portfolioSelected']),
        portfolioCard: {
            get: function(){
                let details = {
                    title: null,
                    pictureSource: null,
                    from: null,
                    until: null,
                    skills: null,
                    body: null
                };

                /* Append portfolio card details according to card selected */
                switch (this.portfolioSelected){
                    /* USB Office Finder */
                    case 'usb-office-finder':
                        details.title = 'Urban Sciences Building Office Finder';
                        details.pictureSource = 'office-finder.png';
                        details.from = 'October 2017';
                        details.until = 'April 2018';
                        details.skills = 'Android Studio, Java, MySQL, Photoshop, UI Design';
                        details.body = '<p>As a team project option for second year students, we were asked to build a navigation app that will help students, staff members and guests to navigate around the newly built Urban Sciences Building. I worked mainly in creating the UI designs and implementing route finding algorithms for this app. Design was done iteratively from hand-drawn, pitched to the team and customer, then transferred to Photoshop and Android Studio. I was also in charge of designing UML diagrams, high level overview of the system and how objects will interact.</p><p>Our team decided to use Java and MySQL to build our logical and database structures. We also implemented a feature to scan room number on doors/walls using camera, which is subsequently returned as either a starting point or a destination. This is achieved by making use of Google Vision API.</p><p>Through this project I learned a lot about a full software engineering cycle&mdash;from requirements analysis up to maintenance. One of which is to experience firsthand Agile working environment that our team used throughout the cycle. Also, I also got the chance to pitch our initial design and requirements on a Dragon&rsquo;s Den attended by panels, comprised of the school&rsquo;s staff and Nissan employees. The app launch was then followed by a trade fair, in which our app was showcased in front of many software engineering professionals around the UK.</p>';
                        return details;
                    
                    /* Discover Indonesia Website */
                    case 'discover-indonesia':
                        details.title = 'Discover Indonesia Website';
                        details.pictureSource = 'discoverindo.png';
                        details.from = 'March 2017';
                        details.skills = 'Web Design, Wix Website Builder';
                        details.body = '<p>Since 2014, Indonesian Students Association in Newcastle (PPI Newcastle) has been running their biggest annual series of events called Discover Indonesia. With its evergrowing acclaim, Discover Indonesia is establishing into one of the biggest events in North East England. About two months prior to the 2017 edition, Discover Indonesia&rsquo;s committee asked me as the then website manager for the association to develop their maiden website.</p><p>I decided to use Wix to ease inheriting this website to my successor, considering they might not necessarily have a web development background. To smoothen user experience and navigation, I built this website in the format of a one-page parallax site. The design is also festive, added with symbols and bright colours, to represent the very heart of these events.</p><p>A few design adjustments were also made given the considerable amount of media embedded. For example, feeding visibility of system status whenever display is affected. As seen in the screenshot above (left laptop), a placeholder saying, "We&rsquo;re loading things up, watch this space!", will be shown until the required video is fully loaded. This will allow users to notice that an expected content is currently unavailable to display.</p><p>Building Discover Indonesia&rsquo;s website was a good learning experience for me in terms of producing an adaptive design, especially under a new environment such as Wix. I had to consider thoroughly the maintenance of the website, which is an important and unavoidable aspect of any website or software development. Also, having only a week or two&mdash;before the event&rsquo;s marketing launch&mdash;to build this website served as a valuable experience of real life working environment and how to deal with client&rsquo;s requests efficiently.</p>';
                        return details;

                    /* WebSiteseeing Tour*/
                    case 'websiteseeing-tour':
                        details.title = 'WebSiteseeing Tour';
                        details.pictureSource = 'websiteseeing.png';
                        details.from = 'March';
                        details.until = 'April 2017';
                        details.skills = 'HTML, CSS, JavaScript, Web Design';
                        details.body = '<p>As part of my first year project, I created this tutorial website which explains briefly about the evolution and uses of HTML, CSS and JavaScript. This website also offers a 15 numbered quiz that users can attempt and have their results returned in a dialog box.</p><p>One of the main inspiration to my design decisions was Domino&rsquo;s delivery website. An interactive bot takes us through the steps of preparing our order in real-time, which provides relevant insights to user in an enganging way. Further inspiration came from city sightseeing buses that I sometimes took during my holidays, where different tour packages are normally represented by buses in differing colours.</p><p>Combining these ideas, I decided to design this website as if users were taking tour packages around a web-cultured city. Users have further selection of places to visit, namely the museum (to learn about the language&rsquo;s history), factory (to learn about its role in web development) and the colosseum (where users are challenged to take a quiz to recap what they have learned).</p><p>I learned a great deal about web design and its implementation through this project, both in technical and non-technical aspects. Initially, I had to create a pitch document to explain how I want to develop the website and what technologies I would use to achieve this. At the end of the project, I created a review document in reflection to the initial pitch document and compared the two. This taught me about the importance of requirements and design analysis before starting a project, as well as reviewing the whole process and finding ways to improve.</p>';
                        return details;

                    /* Hotel Report Generator */
                    case 'hotel-report':
                        details.title = 'Hotel Report Generator';
                        details.pictureSource = 'hotel.png';
                        details.from = 'November';
                        details.until = 'December 2016';
                        details.skills = 'Java';
                        details.body = '<p>I wrote this simple hotel report generator to demonstrate the interactions between Java classes and their object-oriented behaviour, mainly through the use of getter and setter methods. The main features of this program are to generate a report for either an auto-generated hotel (with random details) or a user-created hotel. Within this short textual report, users will be able to see each room&rsquo;s details and availabilty.</p><p>This generator is built on top of 6 separate classes&mdash;some of which represent real world objects, such as beds and rooms. A Bed object contains information about its size: either single or double, which in turn tells about the maximum number of person it can hold. One or more Bed objects are then stored in a list in a Room object. The maximum number of person is also calculated to find the maximum occupancy for each room. Room objects also store information about their vacancy to decide whether a hotel has vacancy or not.</p>';
                        return details;

                    default:
                        return details;
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.modal-body {
    .body-header {
        text-align: center;
        margin-bottom: 1rem;
        
        img {
            margin: 1rem;
            width: 80%;
            height: auto;
        }

        h4 {
            font-size: 0.85rem;
            font-weight: 700;
            color: $colour-grey-primary;

            .fas {
                margin-right: 3px;
                color: $colour-blue-light;
            }
        }
    }

    .body-text {
        /deep/ p {
            text-indent: 2rem;
        }

        &-end {
            text-align: center;

            .fas {
                font-size: 0.75rem;
                margin: 0.2rem 0.2rem 1.8rem;
            }
        }
    }
}
</style>
